import type { ReactNode } from 'react';

import { getIn } from 'formik';
import type { AnyAction, Dispatch } from 'redux';

import { updateField } from '../../store/customer';
import { clearFieldError, setErrorFields } from '../../store/fieldError';
import { contactDeliveryType } from '../../utils/addressHelpers';
import { getNestedProperty } from '../FieldInput';


type UnknownRecord = Record<string, unknown>;

type FormikLike = {
  form: {
    setFieldTouched: (name: string, touched: boolean) => Promise<UnknownRecord>;
    setFieldValue: (name: string, value: unknown, shouldValidate?: boolean) => void;
    errors: UnknownRecord;
    touched: UnknownRecord;
  };
};

type InputChangeEvent = {
  target: {
    name: string;
    value: string;
  };
};

type InputProviderChildrenArgs = {
  hasError: boolean;
  errorMessage: unknown;
  handleOnChange: (event: InputChangeEvent) => void;
  handleOnBlur: () => void;
  isTouched: boolean;
};


type InputProviderProps = {
  formik: FormikLike;
  name: string;
  dispatch: Dispatch<AnyAction>;
  store: { customer: UnknownRecord };
  apiService: {
    updateCustomer: (customer: UnknownRecord, dispatch: Dispatch<AnyAction>) => void;
  };
  onChange?: (args: {
    value: unknown;
    dispatch: Dispatch<AnyAction>;
    formik: FormikLike;
    apiService: unknown;
    store: unknown;
  }) => void;
  disableAutoSave?: boolean;
  children: (args: InputProviderChildrenArgs) => ReactNode;
};

const InputProvider = ({
  formik,
  name,
  dispatch,
  store,
  apiService,
  onChange,
  children,
  disableAutoSave
}: InputProviderProps) => {
  const errorMessage = getIn(formik.form.errors, name);
  const hasError = errorMessage !== undefined;
  const isTouched = getIn(formik.form.touched, name) !== undefined;

  const handleOnBlur = () => {
    formik.form.setFieldTouched(name, true).then((errorMap) => {
      const fieldError = getIn(errorMap, name);

      if (fieldError === undefined) {
        dispatch(clearFieldError(name));
        return;
      }

      if (name.split('.').pop() === 'taxNumber1') {
        const address = getAddress(store.customer, name);
        const matchingKeys = getTax(address);

        if (Object.keys(matchingKeys).length > 1) {
          dispatch(clearFieldError(name));

          const keys = Object.keys(matchingKeys);

          for (let i = 0; i < keys.length - 1; i++) {
            const currentKey = keys[i];
            const nextKey = keys[i + 1];
            const nextValue = matchingKeys[nextKey];

            if (i === 0) {
              formik.form.setFieldValue(name, nextValue);
            }

            dispatch(updateField({ name: currentKey, value: nextValue }));
          }

          dispatch(updateField({ name: keys[keys.length - 1], value: '' }));
          apiService.updateCustomer(store.customer, dispatch);
          return;
        }
      }

      dispatch(setErrorFields(errorMap));
    });

    if (
      (name.includes('deliveryDetails') && !disableAutoSave) ||
      (!disableAutoSave && !hasError)
    ) {
      apiService.updateCustomer(store.customer, dispatch);
    }
  };

  const handleOnChange = (event: InputChangeEvent) => {
    let { name: fieldName, value } = event.target;

    if (fieldName.includes('email')) {
      value = value.replace(/ /g, '');
    }

    dispatch(updateField({ name: fieldName, value }));

    if (
      fieldName.includes('coa') ||
      fieldName.includes('packingList') ||
      fieldName.includes('orderConfirmation') ||
      fieldName.includes('invoiceDelivery')
    ) {
      contactDeliveryType(fieldName, dispatch);
    }

    if (fieldName.includes('deliveryDetails')) {
      formik.form.setFieldValue(
        fieldName,
        value,
        !window.location.href.includes('review')
      );
    } else {
      formik.form.setFieldValue(fieldName, value);
    }

    onChange?.({
      value,
      dispatch,
      formik,
      apiService,
      store
    });
  };

  return children({
    hasError,
    errorMessage,
    handleOnBlur,
    handleOnChange,
    isTouched
  });
};

function getAddress(obj: UnknownRecord, name: string): UnknownRecord {
  const path = name.slice(0, name.lastIndexOf('.'));
  const result = getNestedProperty(obj, path);

  if (typeof result === 'object' && result !== null) {
    return result as UnknownRecord;
  }

  return {} as UnknownRecord;
}


function getTax(address: UnknownRecord): Record<string, string> {
  return Object.entries(address)
    .filter(([key]) => key.startsWith('taxNumber'))
    .reduce<Record<string, string>>((acc, [key, value]) => {
      if (typeof value === 'string') {
        acc[key] = value;
      }
      return acc;
    }, {});
}

export default InputProvider;